import React, { useEffect, useRef, useState } from "react";
import { normalizeImageUrl, PRODUCT_PLACEHOLDER } from "@/lib/imageUrl";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

export default function ProductGallery({ images, resetKey, productName }) {
  const gallery = images && images.length ? images : [{ url: PRODUCT_PLACEHOLDER, alt: productName }];
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(false);
  const touchX = useRef(null);

  // Reset to first image whenever the driving option (e.g. colour) changes
  useEffect(() => { setActive(0); }, [resetKey]);
  const clamp = (i) => (i + gallery.length) % gallery.length;
  const go = (d) => setActive((i) => clamp(i + d));

  // keyboard nav in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const src = (i) => normalizeImageUrl(gallery[Math.min(i, gallery.length - 1)].url);
  const alt = (i) => gallery[i]?.alt || productName;

  return (
    <div>
      <div className="relative aspect-square bg-slate-50 rounded-sm overflow-hidden border border-slate-200 group"
        data-testid="product-gallery" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <button type="button" onClick={() => { setLightbox(true); setZoom(false); }} aria-label="Zoom image"
          data-testid="gallery-zoom" className="w-full h-full cursor-zoom-in">
          <img src={src(active)} alt={alt(active)} className="w-full h-full object-cover" />
        </button>
        <span className="absolute top-2 right-2 bg-white/85 rounded-full p-1.5 pointer-events-none"><ZoomIn className="w-4 h-4 text-[#0A192F]" /></span>
        {gallery.length > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous image" data-testid="gallery-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full p-1.5 shadow"><ChevronLeft className="w-5 h-5 text-[#0A192F]" /></button>
            <button type="button" onClick={() => go(1)} aria-label="Next image" data-testid="gallery-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full p-1.5 shadow"><ChevronRight className="w-5 h-5 text-[#0A192F]" /></button>
          </>
        )}
      </div>

      {gallery.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto" role="tablist" aria-label="Product image thumbnails">
          {gallery.map((img, i) => (
            <button key={img.id || i} type="button" role="tab" aria-selected={active === i}
              onClick={() => setActive(i)} data-testid={`thumb-${i}`}
              className={`w-16 h-16 shrink-0 rounded-sm overflow-hidden border-2 ${active === i ? "border-[#E63946]" : "border-transparent hover:border-slate-300"}`}>
              <img src={normalizeImageUrl(img.url)} alt={img.alt || `View ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex flex-col" role="dialog" aria-modal="true"
          aria-label="Product image viewer" data-testid="gallery-lightbox">
          <div className="flex justify-end p-4">
            <button type="button" onClick={() => setLightbox(false)} aria-label="Close viewer" autoFocus
              data-testid="lightbox-close" className="text-white/80 hover:text-white"><X className="w-7 h-7" /></button>
          </div>
          <div className="flex-1 relative flex items-center justify-center overflow-auto px-4"
            onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <img src={src(active)} alt={alt(active)} onClick={() => setZoom((z) => !z)}
              className={`max-h-[75vh] object-contain transition-transform duration-200 ${zoom ? "scale-[1.8] cursor-zoom-out" : "cursor-zoom-in"}`} />
            {gallery.length > 1 && (
              <>
                <button type="button" onClick={() => go(-1)} aria-label="Previous image" data-testid="lightbox-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 rounded-full p-2"><ChevronLeft className="w-6 h-6 text-white" /></button>
                <button type="button" onClick={() => go(1)} aria-label="Next image" data-testid="lightbox-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 rounded-full p-2"><ChevronRight className="w-6 h-6 text-white" /></button>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 justify-center p-4 overflow-x-auto">
              {gallery.map((img, i) => (
                <button key={img.id || i} type="button" onClick={() => setActive(i)} aria-label={`View image ${i + 1}`}
                  className={`w-14 h-14 shrink-0 rounded-sm overflow-hidden border-2 ${active === i ? "border-white" : "border-transparent opacity-60"}`}>
                  <img src={normalizeImageUrl(img.url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
